import React, { useState, useEffect } from 'react';
import storageService from 'services/storage.service';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import AccessibilityIcon from '@material-ui/icons/Accessibility';
import ViewListIcon from '@material-ui/icons/ViewList';
import PageviewIcon from '@material-ui/icons/Pageview';
import PeopleIcon from '@material-ui/icons/People';
import ProfileDropDown from './ProfileDropDown';
import TopBarDropdown from './TopBarDropdown';
import './TopBar.css';
import OpttyLogo from '../../../logo.png';

const TopBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isProd = process.env.REACT_APP_NODE_ENV === 'production';
  const title = isProd ? 'Optty Admin' : 'UAT Admin';
  const { t } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <div className={`top-navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="top-navbar-container">
        <div className="navbar-left">
          <button
            aria-label="Toggle menu"
            className="navbar-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            type="button"
          />
          <NavLink
            className="navbar-brand"
            to="/app"
          >
            <img
              alt="Optty Logo"
              className="brand-logo"
              src={OpttyLogo}
            />
            <div className="brand-text">
              <h1 className="brand-title">{title}</h1>
              <span className="brand-subtitle">Portal</span>
            </div>
          </NavLink>
        </div>

        <div className={`navbar-actions ${menuOpen ? 'active' : ''}`}>
          <TopBarDropdown
            basePath="/app/onboarding"
            icon={<AccessibilityIcon />}
            isProd={isProd}
            onLinkClick={handleLinkClick}
            subLinks={[
              {
                label: 'Child',
                to: '/app/onboarding/child',
                isActive: (pn) => ['', '/', '/app/onboarding', '/app/onboarding/child'].includes(pn),
              },
              { label: 'Parent', to: '/app/onboarding/parent' },
              { label: 'Grandparent', to: '/app/onboarding/grandparent' },
            ]}
            title={t('Onboarding')}
          />
          <TopBarDropdown
            basePath="/app/invoicing"
            icon={<ReceiptIcon />}
            isProd={isProd}
            onLinkClick={handleLinkClick}
            subLinks={[
              {
                label: 'Commercial Model',
                to: '/app/invoicing/commercial-model',
                isActive: (pn) => pn.includes('/app/invoicing/commercial-model') || pn.startsWith('/app/invoicing/details'),
              },
              { label: 'Referral Agreements', to: '/app/invoicing/referralAgreement' },
              { label: 'Generate', to: '/app/invoicing/generate' },
            ]}
            title={t('Invoicing')}
          />
          <NavLink
            activeClassName={isProd ? 'prod-active' : 'sandbox-active'}
            className="menu-link"
            isActive={() => ['/app/user-management'].some((x) => pathname.includes(x))}
            onClick={handleLinkClick}
            to="/app/user-management"
          >
            <PeopleIcon /> <span>{t('User Management')}</span>
          </NavLink>
          <NavLink
            activeClassName={isProd ? 'prod-active' : 'sandbox-active'}
            className="menu-link"
            isActive={() => ['/app/auditLog'].some((x) => pathname.includes(x))}
            onClick={handleLinkClick}
            to="/app/auditLog"
          >
            <AssessmentIcon /> <span>{t('Audit Log')}</span>
          </NavLink>
          <NavLink
            activeClassName={isProd ? 'prod-active' : 'sandbox-active'}
            className="menu-link"
            isActive={() => ['/app/order-details'].some((x) => pathname.includes(x))}
            onClick={handleLinkClick}
            to="/app/order-details"
          >
            <PageviewIcon /> <span>{t('Order Details')}</span>
          </NavLink>
          <TopBarDropdown
            basePath="/app/operations"
            icon={<ViewListIcon />}
            isProd={isProd}
            onLinkClick={handleLinkClick}
            subLinks={[
              {
                label: 'Announcements',
                to: '/app/announcements',
                isActive: (pn: string) => pn.includes('/app/announcements'),
              },
              { label: 'Grandparent Configuration', to: '/app/operations/ringfencing-grandparents' },
              { label: 'Menu Management', to: '/app/operations/menu-management' },
              { label: 'APM Configurations', to: '/app/operations/apm-configurations' },
              {
                label: 'Feature Matrix',
                to: '/app/payment-methods/feature-matrix',
                isActive: (pn: string) => pn.includes('/app/payment-methods'),
              },
              {
                label: 'Self-Healing',
                to: '/app/self-healing',
                isActive: (pn: string) => pn.includes('/app/self-healing'),
              },
              {
                label: 'Website Content',
                to: '/app/website-content',
                isActive: (pn: string) => pn.includes('/app/website-content'),
              },
            ]}
            title={t(' Operations')}
          />
        </div>

        <div className="navbar-right">
          <ProfileDropDown firstName={storageService.getUserName() || ''} />
        </div>
      </div>
    </div>
  );
};

export default TopBar;
